import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCalendarAlt, 
  faUser, 
  faEnvelope, 
  faPhone, 
  faMapMarkerAlt,
  faCreditCard,
  faTruck,
  faFileInvoice,
  faCheck,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { formatDateShort } from '../../utils/dateUtils';
import PlaceholderImage from '../common/PlaceholderImage';

const OrderDetails = ({ order, onClose, onStatusUpdate }) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    let dateObj;
    if (typeof date === 'string') {
      // Handle date strings like "2025-07-06" by creating date in local timezone
      if (date.includes('-') && date.length === 10) {
        const [year, month, day] = date.split('-');
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        dateObj = new Date(date);
      }
    } else {
      // For Firebase Timestamp objects or Date objects
      dateObj = date.toDate ? date.toDate() : new Date(date);
    }
    
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return dateObj.toLocaleString(undefined, options);
  };

  // Format date for delivery dates (date only, no time)
  const formatDeliveryDate = (date) => {
    if (!date) return 'N/A';
    
    let dateObj;
    if (typeof date === 'string') {
      // Handle date strings like "2025-07-06" by creating date in local timezone
      if (date.includes('-') && date.length === 10) {
        const [year, month, day] = date.split('-');
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        dateObj = new Date(date);
      }
    } else {
      // For Firebase Timestamp objects or Date objects
      dateObj = date.toDate ? date.toDate() : new Date(date);
    }
    
    const options = { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' };
    return dateObj.toLocaleDateString(undefined, options);
  };
  
  // Get status class for styling
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };
  
  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await onStatusUpdate(newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Generate invoice
  const handleGenerateInvoice = () => {
    // Create a new window for printing the invoice
    const printWindow = window.open('', '_blank');
    
    // Get current date for the invoice
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Extract payment information
    const paymentOption = order.paymentOption || order.paymentInfo?.paymentOption;
    const fullTotal = order.paymentInfo?.fullTotal || order.total || (order.pricing?.total) || 0;
    const paidAmount = order.paymentInfo?.paidAmount || order.amountPaid || 0;
    const remainingAmount = order.paymentInfo?.remainingAmount || order.amountDue || 0;
    const isTestOrder = order.paymentInfo?.isTestOrder || order.isTestOrder || false;
    const isDepositPayment = paymentOption === 'deposit';
    const hasRemainingBalance = remainingAmount > 0;
    
    // Get customer name
    const customerName = (() => {
      if (order.customer?.name) return order.customer.name;
      if (order.customerInfo?.name) return order.customerInfo.name;
      if (order.customer?.firstName && order.customer?.lastName) {
        return `${order.customer.firstName} ${order.customer.lastName}`.trim();
      }
      if (order.customerInfo?.firstName && order.customerInfo?.lastName) {
        return `${order.customerInfo.firstName} ${order.customerInfo.lastName}`.trim();
      }
      return 'Unknown Customer';
    })();
    
    // Get address information
    const getAddressField = (field) => {
      return order.delivery?.[field] || 
             order.deliveryInfo?.[field] || 
             order.customerInfo?.address?.[field] || 
             order.customer?.address?.[field] || 
             order.address?.[field] ||
             (field === 'address' && typeof order.delivery?.address === 'string' ? order.delivery.address : null) ||
             (field === 'address' && typeof order.deliveryInfo?.address === 'string' ? order.deliveryInfo.address : null) ||
             (field === 'address' && order.addressFormatted);
    };
    
    const address = getAddressField('address') || getAddressField('line1') || getAddressField('street');
    const city = getAddressField('city');
    const state = getAddressField('state');
    const zipCode = getAddressField('zipCode') || getAddressField('postal_code') || getAddressField('zip');
    const deliveryDate = getAddressField('date') || getAddressField('deliveryDate') || order.rentalDate || order.deliveryDate;
    const deliveryTime = getAddressField('time') || getAddressField('deliveryTime') || order.rentalTime || order.deliveryTime || order.startTime;
    const phone = order.customer?.phone || order.customerInfo?.phone || '';
    
    // Create the invoice HTML
    const invoiceContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rowe Bros - Delivery Invoice</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            line-height: 1.6;
            color: #333;
          }
          .invoice-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #1E88E5;
            padding-bottom: 20px;
          }
          .invoice-header h1 {
            margin: 0;
            color: #1E88E5;
            font-size: 2.5em;
          }
          .invoice-header p {
            margin: 5px 0;
            color: #666;
            font-size: 1.1em;
          }
          .invoice-details {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
          }
          .invoice-info, .customer-info {
            width: 48%;
          }
          .invoice-info h3, .customer-info h3 {
            color: #1E88E5;
            margin-bottom: 10px;
            border-bottom: 2px solid #1E88E5;
            padding-bottom: 5px;
          }
          .info-line {
            margin: 8px 0;
            display: flex;
            justify-content: space-between;
          }
          .info-label {
            font-weight: bold;
            width: 40%;
          }
          .info-value {
            width: 60%;
            text-align: right;
          }
          .delivery-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
            border-left: 4px solid #1E88E5;
          }
          .delivery-section h3 {
            color: #1E88E5;
            margin-top: 0;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .payment-section {
            background: ${hasRemainingBalance ? '#fff3e0' : '#e8f5e9'};
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
            border: 3px solid ${hasRemainingBalance ? '#ff9800' : '#4caf50'};
          }
          .payment-section h3 {
            color: ${hasRemainingBalance ? '#ef6c00' : '#2e7d32'};
            margin-top: 0;
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 1.3em;
          }
          .payment-highlight {
            background: ${hasRemainingBalance ? '#ff9800' : '#4caf50'};
            color: white;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
            font-size: 1.2em;
            font-weight: bold;
            margin: 15px 0;
          }
          .payment-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin: 15px 0;
          }
          .payment-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #ddd;
          }
          .payment-item.total {
            font-weight: bold;
            font-size: 1.1em;
            border-bottom: 2px solid #333;
            margin-top: 10px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          .items-table th, .items-table td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          .items-table th {
            background-color: #1E88E5;
            color: white;
            font-weight: bold;
          }
          .items-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #ddd;
            text-align: center;
            font-size: 0.9em;
            color: #666;
          }
          .driver-instructions {
            background: #e3f2fd;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
            border-left: 4px solid #2196f3;
          }
          .driver-instructions h3 {
            color: #1976d2;
            margin-top: 0;
          }
          .driver-instructions ul {
            margin: 10px 0;
            padding-left: 20px;
          }
          .driver-instructions li {
            margin: 5px 0;
          }
          @media print {
            .no-print {
              display: none;
            }
            body {
              margin: 10px;
            }
            .payment-section, .driver-instructions {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <h1>🚚 DELIVERY INVOICE</h1>
          <p>Rowe Bros Rental Equipment</p>
          <p>Generated on ${formattedDate}</p>
        </div>
        
        <div class="invoice-details">
          <div class="invoice-info">
            <h3>📋 Order Information</h3>
            <div class="info-line">
              <span class="info-label">Order ID:</span>
              <span class="info-value">${order.id}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Date Placed:</span>
              <span class="info-value">${formatDate(order.createdAt)}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Status:</span>
              <span class="info-value">${order.status || 'Pending'}</span>
            </div>
          </div>
          
          <div class="customer-info">
            <h3>👤 Customer Information</h3>
            <div class="info-line">
              <span class="info-label">Name:</span>
              <span class="info-value">${customerName}</span>
            </div>
            ${phone ? `
            <div class="info-line">
              <span class="info-label">Phone:</span>
              <span class="info-value">${phone}</span>
            </div>
            ` : ''}
            <div class="info-line">
              <span class="info-label">Email:</span>
              <span class="info-value">${order.customer?.email || order.customerInfo?.email || 'N/A'}</span>
            </div>
          </div>
        </div>
        
        <div class="delivery-section">
          <h3>📍 Delivery Information</h3>
          <div class="info-line">
            <span class="info-label">Address:</span>
            <span class="info-value">${address || 'N/A'}</span>
          </div>
          ${(city || state || zipCode) ? `
          <div class="info-line">
            <span class="info-label">City/State/Zip:</span>
            <span class="info-value">${[city, state, zipCode].filter(Boolean).join(', ')}</span>
          </div>
          ` : ''}
          ${deliveryDate ? `
          <div class="info-line">
            <span class="info-label">Delivery Date:</span>
            <span class="info-value">${formatDeliveryDate(deliveryDate)}</span>
          </div>
          ` : ''}
          ${deliveryTime ? `
          <div class="info-line">
            <span class="info-label">Delivery Time:</span>
            <span class="info-value">${deliveryTime}</span>
          </div>
          ` : ''}
        </div>
        
        <div class="payment-section">
          <h3>${hasRemainingBalance ? '💸 PAYMENT REQUIRED' : '✅ PAYMENT COMPLETE'}</h3>
          
          <div class="payment-highlight">
            ${hasRemainingBalance 
              ? `COLLECT $${remainingAmount.toFixed(2)} FROM CUSTOMER` 
              : 'NO PAYMENT REQUIRED - FULLY PAID'}
          </div>
          
          <div class="payment-details">
            <div class="payment-item">
              <span>Payment Type:</span>
              <span>${isTestOrder ? 'Test Order' : 
                     isDepositPayment ? 'Deposit Payment' : 'Full Payment'}</span>
            </div>
            <div class="payment-item">
              <span>Order Total:</span>
              <span>$${fullTotal.toFixed(2)}</span>
            </div>
            <div class="payment-item">
              <span>Amount Paid:</span>
              <span>$${paidAmount.toFixed(2)}</span>
            </div>
            <div class="payment-item total">
              <span>${hasRemainingBalance ? 'AMOUNT DUE:' : 'BALANCE:'}</span>
              <span>$${remainingAmount.toFixed(2)}</span>
            </div>
          </div>
          
          ${hasRemainingBalance ? `
          <p><strong>Note:</strong> Customer paid a $${isTestOrder ? '1' : '50'} deposit. 
          Collect the remaining balance of <strong>$${remainingAmount.toFixed(2)}</strong> upon delivery.</p>
          ` : `
          <p><strong>✅ All Set!</strong> Customer paid the full amount. 
          No additional payment required.</p>
          `}
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.items?.map(item => `
              <tr>
                <td>${item.name}</td>
                <td>${item.quantity || 1}</td>
                <td>$${(item.price || 0).toFixed(2)}</td>
                <td>$${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
              </tr>
            `).join('') || '<tr><td colspan="4">No items found</td></tr>'}
          </tbody>
        </table>
        
        <div class="driver-instructions">
          <h3>🚛 Driver Instructions</h3>
          <ul>
            <li><strong>Setup:</strong> Set up all equipment according to safety guidelines</li>
            <li><strong>Payment:</strong> ${hasRemainingBalance 
              ? `Collect $${remainingAmount.toFixed(2)} from customer (cash, check, or card)` 
              : 'No payment collection required - order is fully paid'}</li>
            <li><strong>Inspection:</strong> Have customer inspect equipment before leaving</li>
            <li><strong>Documentation:</strong> Take photos of setup and get customer signature</li>
            <li><strong>Contact:</strong> Call office if any issues arise</li>
          </ul>
        </div>
        
        <div class="footer">
          <p>Rowe Bros Rental Equipment - Professional Equipment Rental Services</p>
          <p>For questions or issues, contact: (260) 226-5640</p>
          <button class="no-print" onclick="window.print()" style="
            background: #1E88E5;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 5px;
            cursor: pointer;
            font-size: 16px;
            margin-top: 20px;
          ">Print Invoice</button>
        </div>
      </body>
      </html>
    `;
    
    // Write the content to the new window and prepare for printing
    printWindow.document.open();
    printWindow.document.write(invoiceContent);
    printWindow.document.close();
    
    // Automatically print after the content is loaded
    printWindow.onload = function() {
      // Give a moment for styles to apply
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    };
  };

  // Handle overlay click to close modal
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('order-details')) {
      onClose();
    }
  };
  
  return (
    <div className="order-details" onClick={handleOverlayClick}>
      <div className="order-details-container">
      <div className="order-details-header">
        <div>
          <h2>Order Details</h2>
          <p className="order-id">Order ID: {order.id}</p>
        </div>
        <button className="icon-button close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      
      <div className="order-details-content">
        <div className="order-details-row">
          <div className="order-details-column">
            <div className="detail-card">
              <h3>Order Information</h3>
              <div className="detail-item">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <div>
                  <span className="detail-label">Date Placed</span>
                  <span className="detail-value">{formatDate(order.createdAt)}</span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faTruck} />
                <div>
                  <span className="detail-label">Status</span>
                  <span className={`detail-value order-status ${getStatusClass(order.status)}`}>
                    {order.status || 'Unknown'}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faCreditCard} />
                <div>
                  <span className="detail-label">Payment Method</span>
                  <span className="detail-value">{order.paymentMethod || 'Not specified'}</span>
                </div>
              </div>
            </div>
            
            <div className="detail-card">
              <h3>Customer Information</h3>
              <div className="detail-item">
                <FontAwesomeIcon icon={faUser} />
                <div>
                  <span className="detail-label">Name</span>
                  <span className="detail-value">
                    {(() => {
                      // Try multiple name formats in order of preference
                      if (order.customer?.name) return order.customer.name;
                      if (order.customerInfo?.name) return order.customerInfo.name;
                      if (order.customer?.firstName && order.customer?.lastName) {
                        return `${order.customer.firstName} ${order.customer.lastName}`.trim();
                      }
                      if (order.customerInfo?.firstName && order.customerInfo?.lastName) {
                        return `${order.customerInfo.firstName} ${order.customerInfo.lastName}`.trim();
                      }
                      if (order.customer?.displayName) return order.customer.displayName;
                      if (order.customerInfo?.displayName) return order.customerInfo.displayName;
                      if (order.customer?.fullName) return order.customer.fullName;
                      if (order.customerInfo?.fullName) return order.customerInfo.fullName;
                      return 'Unknown';
                    })()}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faEnvelope} />
                <div>
                  <span className="detail-label">Email</span>
                  <span className="detail-value">
                    {order.customer?.email || 
                     order.customerInfo?.email || 
                     order.customer?.emailAddress ||
                     order.customerInfo?.emailAddress ||
                     'Not provided'}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faPhone} />
                <div>
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">
                    {order.customer?.phone || 
                     order.customerInfo?.phone || 
                     order.customer?.phoneNumber ||
                     order.customerInfo?.phoneNumber ||
                     'Not provided'}
                  </span>
                </div>
              </div>
              {(order.customer?.customerId || order.customerInfo?.customerId || order.customerId || order.userId) && (
                <div className="detail-item">
                  <FontAwesomeIcon icon={faUser} />
                  <div>
                    <span className="detail-label">Customer ID</span>
                    <span className="detail-value">
                      {order.customer?.customerId || order.customerInfo?.customerId || order.customerId || order.userId}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="detail-card">
              <h3>Shipping Address</h3>
              {(() => {
                // Check all possible address sources
                const hasDelivery = order.delivery;
                const hasDeliveryInfo = order.deliveryInfo;
                const hasCustomerAddress = order.customerInfo?.address;
                const hasCustomerInfoAddress = order.customer?.address;
                
                                 // Get address components from various sources
                 const getAddressField = (field) => {
                   return order.delivery?.[field] || 
                          order.deliveryInfo?.[field] || 
                          order.customerInfo?.address?.[field] || 
                          order.customer?.address?.[field] || 
                          order.address?.[field] || // Check top-level address object
                          (typeof order.delivery?.address === 'object' ? order.delivery.address[field] : null) ||
                          (typeof order.deliveryInfo?.address === 'object' ? order.deliveryInfo.address[field] : null) ||
                          (field === 'address' && typeof order.delivery?.address === 'string' ? order.delivery.address : null) ||
                          (field === 'address' && typeof order.deliveryInfo?.address === 'string' ? order.deliveryInfo.address : null) ||
                          (field === 'address' && order.addressFormatted) || // Check for formatted address string
                          (field === 'address' && order.address?.street); // Check for street in address object
                 };

                                 const address = getAddressField('address') || getAddressField('line1') || getAddressField('street') || order.addressFormatted;
                 const city = getAddressField('city');
                 const state = getAddressField('state');
                 const zipCode = getAddressField('zipCode') || getAddressField('postal_code') || getAddressField('zip');
                const deliveryDate = getAddressField('date') || getAddressField('deliveryDate') || order.rentalDate || order.deliveryDate;
                const deliveryTime = getAddressField('time') || getAddressField('deliveryTime') || order.rentalTime || order.deliveryTime || order.startTime;
                const instructions = getAddressField('instructions') || getAddressField('specialInstructions') || getAddressField('deliveryInstructions');

                // Check if we have any address information
                const hasAnyAddressInfo = hasDelivery || hasDeliveryInfo || hasCustomerAddress || hasCustomerInfoAddress || address || city || state || zipCode;

                return hasAnyAddressInfo ? (
                <div className="detail-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <div>
                      {address && (
                        <span className="detail-value address-line">
                          {address}
                        </span>
                      )}
                      {(city || state || zipCode) && (
                    <span className="detail-value address-line">
                          {[city, state, zipCode].filter(Boolean).join(', ')}
                    </span>
                      )}
                      {deliveryDate && (
                      <span className="detail-value address-line">
                          <strong>Delivery Date:</strong> {formatDeliveryDate(deliveryDate)}
                      </span>
                      )}
                      {deliveryTime && (
                      <span className="detail-value address-line">
                          <strong>Delivery Time:</strong> {deliveryTime}
                      </span>
                    )}
                      {instructions && (
                      <span className="detail-value address-line">
                          <strong>Instructions:</strong> {instructions}
                      </span>
                    )}
                      {!address && !city && !state && !zipCode && (
                      <span className="detail-value address-line">
                          Address information available but not properly formatted in order data
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="no-data">No shipping address provided</p>
                );
              })()}
            </div>
            
            {/* Waiver Information Section */}
            {order.liabilityWaiver && (
              <div className="detail-card">
                <h3>Waiver & Terms Information</h3>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <div>
                    <span className="detail-label">Terms Agreed</span>
                    <span className={`detail-value ${order.liabilityWaiver.termsAgreed ? 'status-completed' : 'status-pending'}`}>
                      {order.liabilityWaiver.termsAgreed ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <div>
                    <span className="detail-label">Surface Type</span>
                    <span className="detail-value">{order.liabilityWaiver.surfaceType || 'Not specified'}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faTruck} />
                  <div>
                    <span className="detail-label">Power Within 100 Feet</span>
                    <span className="detail-value">
                      {order.liabilityWaiver.powerWithin50Feet === true ? 'Yes' : 
                       order.liabilityWaiver.powerWithin50Feet === false ? 'No' : 'Not specified'}
                    </span>
                  </div>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faCreditCard} />
                  <div>
                    <span className="detail-label">Damage Waiver Purchased</span>
                    <span className={`detail-value ${order.liabilityWaiver.purchaseDamageWaiver ? 'status-completed' : 'status-pending'}`}>
                      {order.liabilityWaiver.purchaseDamageWaiver ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <div>
                    <span className="detail-label">Overnight Delivery</span>
                    <span className={`detail-value ${order.liabilityWaiver.overnightUpcharge ? 'status-completed' : 'status-pending'}`}>
                      {order.liabilityWaiver.overnightUpcharge ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="order-details-column">
            <div className="detail-card order-items-card">
              <h3>Order Items</h3>
              {order.items && order.items.length > 0 ? (
                <div className="order-items-list">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="product-info">
                              <div className="product-thumbnail-wrapper">
                                <PlaceholderImage 
                                  src={item.image || item.mainImage || item.imageUrl}
                                  alt={item.name}
                                  className="product-thumbnail"
                                  fallbackName={item.name}
                                />
                              </div>
                              <div className="product-details">
                                <p className="product-name">{item.name}</p>
                                {item.options && (
                                  <p className="product-options">
                                    {Object.entries(item.options).map(([key, value]) => 
                                      `${key}: ${value}`
                                    ).join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.price)}</td>
                          <td>{formatCurrency(item.price * item.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="no-data">No items in this order</p>
              )}
            </div>
            
            <div className="detail-card order-summary-card">
              <h3>Order Summary</h3>
              <div className="order-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{formatCurrency(order.subtotal || (order.pricing?.subtotal) || 0)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>{formatCurrency(order.shipping || order.deliveryFee || order.travelFee || (order.pricing?.deliveryFee) || 0)}</span>
                </div>
                {(order.discount > 0 || order.pricing?.discount > 0) && (
                  <div className="summary-row discount">
                    <span>Discount</span>
                    <span>-{formatCurrency(order.discount || order.pricing?.discount || 0)}</span>
                  </div>
                )}
                {(order.damageWaiver > 0 || order.pricing?.damageWaiver > 0) && (
                  <div className="summary-row">
                    <span>Damage Waiver</span>
                    <span>{formatCurrency(order.damageWaiver || order.pricing?.damageWaiver || 0)}</span>
                  </div>
                )}
                {(order.miscFees > 0 || order.pricing?.miscFees > 0) && (
                  <div className="summary-row">
                    <span>Misc Fees</span>
                    <span>{formatCurrency(order.miscFees || order.pricing?.miscFees || 0)}</span>
                  </div>
                )}
                {(order.tax > 0 || order.pricing?.tax > 0) && (
                  <div className="summary-row">
                    <span>Tax</span>
                    <span>{formatCurrency(order.tax || order.pricing?.tax || 0)}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total</span>
                  <span>{formatCurrency(order.total || order.totalAmount || (order.pricing?.total) || 0)}</span>
                </div>
              </div>
            </div>
            
            <div className="detail-card payment-info-card">
              <h3>
                <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '0.5rem' }} />
                Payment Information
              </h3>
              <div className="payment-info">
                {(() => {
                  // Extract payment information from different possible locations
                  const paymentOption = order.paymentOption || order.paymentInfo?.paymentOption;
                  const fullTotal = order.paymentInfo?.fullTotal || order.total || order.totalAmount || (order.pricing?.total) || 0;
                  const paidAmount = order.paymentInfo?.paidAmount || order.amountPaid || 0;
                  const remainingAmount = order.paymentInfo?.remainingAmount || order.amountDue || 0;
                  const isTestOrder = order.paymentInfo?.isTestOrder || order.isTestOrder || false;
                  
                  // Determine payment type and status
                  const isDepositPayment = paymentOption === 'deposit';
                  const isFullPayment = paymentOption === 'full';
                  const hasRemainingBalance = remainingAmount > 0;
                  
                  return (
                    <>
                      <div className="payment-row payment-type">
                        <span className="payment-label">Payment Type</span>
                        <span className={`payment-value payment-badge ${isDepositPayment ? 'deposit' : 'full'}`}>
                          {isTestOrder ? '🧪 Test Order' : 
                           isDepositPayment ? '💳 Deposit Payment' : 
                           isFullPayment ? '💰 Full Payment' : 
                           'Unknown'}
                        </span>
                      </div>
                      
                      <div className="payment-row">
                        <span className="payment-label">Order Total</span>
                        <span className="payment-value">{formatCurrency(fullTotal)}</span>
                      </div>
                      
                      <div className="payment-row paid-amount">
                        <span className="payment-label">Amount Paid</span>
                        <span className="payment-value amount-paid">
                          {formatCurrency(paidAmount)}
                          {isTestOrder && <span className="test-note"> (Test: $1)</span>}
                        </span>
                      </div>
                      
                      {hasRemainingBalance ? (
                        <div className="payment-row remaining-balance">
                          <span className="payment-label">
                            <strong>💸 Collect at Delivery</strong>
                          </span>
                          <span className="payment-value amount-due">
                            <strong>{formatCurrency(remainingAmount)}</strong>
                          </span>
                        </div>
                      ) : (
                        <div className="payment-row payment-complete">
                          <span className="payment-label">
                            <FontAwesomeIcon icon={faCheck} style={{ color: '#28a745', marginRight: '0.5rem' }} />
                            Payment Status
                          </span>
                          <span className="payment-value payment-complete-badge">
                            ✅ Fully Paid
                          </span>
                        </div>
                      )}
                      
                      {isDepositPayment && hasRemainingBalance && (
                        <div className="payment-note">
                          <p>
                            <strong>Note:</strong> Customer paid a ${isTestOrder ? '1' : '50'} deposit. 
                            Collect the remaining balance of <strong>{formatCurrency(remainingAmount)}</strong> at delivery.
                          </p>
                        </div>
                      )}
                      
                      {isFullPayment && !hasRemainingBalance && (
                        <div className="payment-note payment-complete-note">
                          <p>
                            <strong>✅ All Set!</strong> Customer paid the full amount. 
                            No additional payment required at delivery.
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div className="detail-card actions-card">
              <h3>Actions</h3>
              <div className="order-actions">
                <div className="status-actions">
                  <p>Update Status:</p>
                  <div className="status-buttons">
                    {(() => {
                      const currentStatus = (order.status || '').toLowerCase();
                      return (
                        <>
                          <button
                            className={`status-button confirmed ${currentStatus === 'confirmed' ? 'active' : ''}`}
                            onClick={() => handleStatusUpdate('confirmed')}
                            disabled={updatingStatus || currentStatus === 'confirmed'}
                          >
                            Confirmed
                          </button>
                          <button
                            className={`status-button processing ${currentStatus === 'processing' ? 'active' : ''}`}
                            onClick={() => handleStatusUpdate('Processing')}
                            disabled={updatingStatus || currentStatus === 'processing'}
                          >
                            Processing
                          </button>
                          <button
                            className={`status-button completed ${currentStatus === 'completed' ? 'active' : ''}`}
                            onClick={() => handleStatusUpdate('Completed')}
                            disabled={updatingStatus || currentStatus === 'completed'}
                          >
                            Completed
                          </button>
                          <button
                            className={`status-button cancelled ${currentStatus === 'cancelled' ? 'active' : ''}`}
                            onClick={() => handleStatusUpdate('Cancelled')}
                            disabled={updatingStatus || currentStatus === 'cancelled'}
                          >
                            Cancelled
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  {updatingStatus && (
                    <div className="updating-status">
                      <FontAwesomeIcon icon={faSpinner} spin /> Updating status...
                    </div>
                  )}
                </div>
                
                <div className="other-actions">
                  <button className="action-button" onClick={handleGenerateInvoice}>
                    <FontAwesomeIcon icon={faFileInvoice} /> Generate Invoice
                  </button>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
